import React, { useEffect, useMemo } from 'react';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Drawer, Grid } from 'antd';
import { Button, Layout, Menu } from 'antd';
import { dashboardRoutes } from './dashboardRoutes';
import styles from '../../Assets/Styles/Sidebar.module.css';
import { useLocation } from 'react-router-dom';
import { useDashboardNavigate } from '../../hooks/useDashboardNavigate';
import { useTheme } from '../../context/ThemeContext';
import { useAllowedRoutes, useSidebarItems } from '../../hooks/useSidebarHooks';
import { findFirstMenuItemKey } from './sidebarHelper';

const { Sider } = Layout;
const { useBreakpoint } = Grid;

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  onMenuSelect: (key: string) => void;
  activeMenu: string;
}

export const Sidebar = ({
  collapsed,
  setCollapsed,
  onMenuSelect,
  activeMenu,
}: SidebarProps): React.ReactNode => {
  const navigateTo = useDashboardNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const { isDarkMode } = useTheme();
  const allowedRoutes = useAllowedRoutes();
  const items = useSidebarItems(collapsed);

  const activeRoute = useMemo(() => {
    // find these routes which contains url in path variable and logged in user role same as in dashboard routes menu
    return allowedRoutes.find(
      (route) => route.path && location.pathname.includes(route.path)
    );
  }, [allowedRoutes, location.pathname]);

  const findMenuKeyFromUrl = () => {
    return (
      allowedRoutes.find((route) => location.pathname.includes(route.path))
        ?.key ?? ''
    );
  };
  useEffect(() => {
    if (!activeRoute && items.length > 0) {
      const menuKey = findFirstMenuItemKey(items);
      const firstMenuItem = allowedRoutes.find(
        (route) => route.key === menuKey
      );

      if (firstMenuItem) {
        navigateTo(firstMenuItem.path);
      }
    }
  }, [activeRoute, allowedRoutes, items, navigateTo]);

  const handleMenuClick = (key: string) => {
    const route = dashboardRoutes.find((r) => r.key === key);
    if (route) {
      navigateTo(route.path);
    }
  };

  const SiderMenuContent = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: isDarkMode ? '#00011d' : '',
      }}
    >
      {screens.lg && (
        // Collapse button
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-end',
            padding: '16px',
            height: '64px',
            flexShrink: 0,
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            style={{ fontSize: '20px', color: '#fff', width: 32, height: 32 }}
            onClick={() => setCollapsed(!collapsed)}
          ></Button>
        </div>
      )}
      <div className="demo-logo-vertical" />
      <div
        className={styles.thinScrollbar}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
        }}
      >
        <Menu
          theme={isDarkMode ? 'light' : 'dark'}
          inlineIndent={40}
          selectedKeys={activeMenu ? [activeMenu] : [findMenuKeyFromUrl()]}
          mode="inline"
          style={{
            minHeight: '100%',
            backgroundColor: isDarkMode ? '#00011d' : '',
          }}
          onClick={(e) => {
            handleMenuClick(e.key);
            onMenuSelect(e.key);
            if (!screens.lg) {
              setCollapsed(true);
            }
          }}
          items={items}
        />
      </div>
    </div>
  );

  return (
    <>
      {screens.lg ? (
        <Sider
          className={styles.sidebarWrapper}
          trigger={null}
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          width={300}
          breakpoint="lg"
          collapsedWidth={screens.lg ? 80 : 0}
          style={{ height: '100%' }}
        >
          {SiderMenuContent}
        </Sider>
      ) : (
        <Drawer
          placement="left"
          closable={false}
          onClose={() => setCollapsed(true)}
          open={!collapsed}
          styles={{ body: { padding: 0 } }}
          width={280}
        >
          {SiderMenuContent}
        </Drawer>
      )}
    </>
  );
};
